import { OktaAuth, type TokenParams } from '@okta/okta-auth-js';
import { ENGINE_API_URL, OKTA_BASE_URL, OKTA_CLIENT_ID } from '../constants/platform';
import { isNullOrEmpty } from './guard';
import type { SSOMetadata } from '../types/auth';
import { AUTH_SERVER_BASE_URL } from '../constants/platform';

export const authClient = new OktaAuth({
    issuer: `${OKTA_BASE_URL}/oauth2/default`,
    clientId: OKTA_CLIENT_ID,
    redirectUri: `${
        ENGINE_API_URL || window.location.origin
    }/api/draw/1/authorization-code/callback`,
    responseType: 'code',
    pkce: false,
});

export const getWithRedirect = async (params?: TokenParams | undefined) => {
    await authClient.token.getWithRedirect(params);
};

export const oktaSignOut = async () => {
    await authClient.signOut();
};

export const getSSOMetadata = () => {
    const cookies = document.cookie.split(';');
    const ssoInfoCookie = cookies.find((value) =>
        value.includes('boomi-flow-sso-metadata-session'),
    );
    if (ssoInfoCookie) {
        const cookieContent = ssoInfoCookie.split('=').pop();
        if (!isNullOrEmpty(cookieContent)) {
            return JSON.parse(decodeURIComponent(cookieContent)) as SSOMetadata;
        }
    }
    return null;
};

declare global {
    interface Window {
        AuthUiClient: {
            Auth: typeof AuthUiClient.Auth;
            AuthError: typeof AuthUiClient.AuthError;
            AuthErrorFactory: typeof AuthUiClient.AuthErrorFactory;
            PlatformJwt: typeof AuthUiClient.PlatformJwt;
            [key: string]: unknown;
        };
        auth: unknown;
    }
}

export const initializeAuthlib = (): void => {
    if (typeof window === 'undefined') return;
    if (getAuthFromWindow() !== null) return;
    if (!isAuthlibLoaded()) return;

    const auth = window.AuthUiClient.Auth.getInstance({
        baseUrl: AUTH_SERVER_BASE_URL,
        defaultRedirectUrl: window.location.href,
        system: 'platform',
        autoRenewToken: true,
        logLevel: process.env['NODE_ENV'] === 'production' ? 'error' : 'debug',
    });

    window.auth = auth;
};

export const getAccountId = async (): Promise<string | undefined> => {
    let accountId: string | undefined;

    // First attempt to get the account ID from the auth object
    try {
        const auth = getAuthFromWindow();
        if (auth?.isAuthenticated()) {
            accountId = await auth.getAccountId();
        }
    } catch (err) {
        console.error(getErrorMessage(err));
    }

    // Fallback attempt to get it from the cookie
    if (!accountId) {
        const cookie = await cookieStore.get('ACCOUNT_ID');
        accountId = cookie?.value;
    }

    return accountId;
};

export const getAuthFromWindow = (): AuthUiClient.Auth | null => {
    if (typeof window === 'undefined' || !window.auth) {
        return null;
    }

    return window.auth as AuthUiClient.Auth;
};

const isAuthlibLoaded = (): boolean => {
    return typeof window !== 'undefined' && window.AuthUiClient?.Auth !== undefined;
};

export const getAuthHeader = async (): Promise<Record<string, string>> => {
    if (hasSsoSession()) {
        return {};
    }

    const token = await getAccessToken();
    // No token (authlib not loaded / no session): send no header rather than "Bearer undefined",
    // which would make the engine reject the request instead of deferring to other auth schemes.
    if (!token) {
        return {};
    }

    return { Authorization: `Bearer ${token}` };
};

// SSO (SAML) sessions authenticate via the builder cookie, not a platform JWT. Detected via the
// readable `boomi-flow-sso-metadata-session` cookie set during SAML login, or - on a fresh visit
// before that cookie exists - the `?sso` bookmark param. Without the bookmark check, the authlib
// `getToken()` call below would redirect an unauthenticated user to the platform login screen
// before Root can send them to SAML.
export const hasSsoSession = (): boolean =>
    isSsoBookmark() ||
    (typeof document !== 'undefined' &&
        document.cookie
            .split(';')
            .some((cookie) => cookie.trim().startsWith('boomi-flow-sso-metadata-session')));

const isSsoBookmark = (): boolean =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('sso');

export const checkAndRestoreSession = async (): Promise<boolean> => {
    const auth = getAuthFromWindow();

    if (!auth) {
        console.log('No auth found');
        return false;
    }

    try {
        const token = await getAccessToken();
        return Boolean(token);
    } catch (error) {
        console.error('Session restore error:', error);
        return false;
    }
};

export const getAccessToken = async (): Promise<string | undefined> => {
    try {
        const auth = getAuthFromWindow();
        const accountId = await getAccountId();

        if (!auth) return;

        const token = await auth.getToken(accountId);

        if (token) {
            await setFallbackCookie(await auth.getAccountId());
        }

        return token;
    } catch (err) {
        console.error(getErrorMessage(err));
        return undefined;
    }
};

const setFallbackCookie = async (accountId: string | undefined) => {
    if (accountId && !(await cookieStore.get('ACCOUNT_ID'))) {
        cookieStore.set({ name: 'ACCOUNT_ID', value: accountId });
    }
};

export const getErrorMessage = (
    error: unknown,
    fallback = 'An unexpected error occurred',
): string => {
    if (error instanceof window.AuthUiClient.AuthError) {
        return error.getUserMessage();
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return fallback;
};

export const switchAccount = async ({ accountId }: { accountId: string }) => {
    await cookieStore.delete('ACCOUNT_ID');
    const auth = getAuthFromWindow();

    await auth?.logout();
    await auth?.getToken(accountId);
    window.location.reload();
};
